import React from "react";
import { Grid, Box, Typography, Stack, useTheme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import img from "../../assets/shop/powder.jpeg";
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SecurityIcon from '@mui/icons-material/Security';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

const steps = [
    {
        icon: <PsychologyIcon sx={{ fontSize: 32 }} />,
        title: "VEDIC WISDOM",
        description: "Ancient astrological knowledge passed down through generations of masters.",
    },
    {
        icon: <AutoAwesomeIcon sx={{ fontSize: 32 }} />,
        title: "ACCURATE PREDICTIONS",
        description: "Precise forecasting based on planetary movements and birth charts.",
    },
    {
        icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
        title: "GUIDANCE WITH CARE",
        description: "Personalized readings delivered with compassion and understanding.",
    },
    {
        icon: <SecurityIcon sx={{ fontSize: 32 }} />,
        title: "LIFE PROTECTION",
        description: "Remedial solutions to navigate challenges and enhance positive energies.",
    },
    {
        icon: <FamilyRestroomIcon sx={{ fontSize: 32 }} />,
        title: "FAMILY WELL-BEING",
        description: "Comprehensive analysis for entire family harmony and prosperity.",
    },
];

const Trataka = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Animation variants to match Courses component
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
    };

    const imageVariants = {
        hidden: { opacity: 0, x: 30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
    };

    const stepVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
        hover: {
            scale: 1.02,
            backgroundColor: "rgba(109, 22, 117, 0.05)",
            transition: { duration: 0.3 }
        }
    };

    return (
        <Box 
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            sx={{ 
                // maxWidth: "1200px", 
                mx: "auto", 
                py: 6, 
                px: 2,
                bgcolor: "rgba(255, 234, 223, 0.3)",
                borderRadius: 3,
                // mb: 4,
            }}
        >
            {/* Header Section */}
            <motion.div variants={itemVariants}>
                <Typography
                    variant="h4"
                    sx={{
                        textAlign: "center",
                        mb: 3,
                        fontWeight: "bold",
                        color: "#6d1675",
                        fontSize: "30px",
                    }}
                >
                    Benefits of <span style={{ color: "#ffc20f" }}>Vedic Astrology</span>
                </Typography>

                {/* Flower Divider */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        maxWidth: 300,
                        mx: "auto",
                        mb: 4,
                    }}
                >
                    <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                    <Box
                        sx={{
                            px: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <LocalFloristIcon sx={{ fontSize: 28, color: "#ffc20f" }} />
                    </Box>
                    <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                </Box>
            </motion.div>

            <Grid container spacing={6} alignItems="center">
                {/* Image Section with shiny glass hover */}
                <Grid size={{xs:12, md:6.5}}>
                    <motion.div variants={imageVariants}>
                        <Box
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                borderRadius: 3,
                                boxShadow: "0 8px 25px rgba(109, 22, 117, 0.2)",
                                cursor: "pointer",
                                border: "4px solid #fff",
                                "&:hover .shiny": {
                                    animation: "shinyMove 0.5s linear forwards",
                                },
                                ".shiny": {
                                    animation: "none",
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src={img}
                                alt="Vedic Astrology and Planetary Wisdom"
                                sx={{ 
                                    width: "100%", 
                                    display: "block",
                                    height: isMobile ? "300px" : "400px",
                                    objectFit: "cover",
                                }}
                            />
                            {/* Shiny overlay */}
                            <Box
                                className="shiny"
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: "-75%",
                                    height: "100%",
                                    width: "20%",
                                    background:
                                        "linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255, 194, 15, 0.3) 50%, rgba(255,255,255,0) 100%)",
                                    transform: "skewX(-25deg)",
                                    pointerEvents: "none",
                                }}
                            />
                        </Box>
                    </motion.div>
                </Grid>

                {/* Content Section */}
                <Grid size={{xs:12, md:5.5}}>
                    <Stack spacing={3}>
                        {steps.map(({ icon, title, description }, idx) => (
                            <motion.div
                                key={idx}
                                variants={stepVariants}
                                whileHover="hover"
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="flex-start"
                                    sx={{ 
                                        p: 2,
                                        borderRadius: 2,
                                        border: "2px solid #f0f0f0",
                                        backgroundColor: "#fff",
                                        boxShadow: "0 2px 10px rgba(109, 22, 117, 0.1)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            borderColor: "#ffc20f",
                                            boxShadow: "0 4px 15px rgba(109, 22, 117, 0.2)",
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: "#6d1675",
                                            borderRadius: 2,
                                            p: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: 60,
                                            minHeight: 60,
                                            color: "#ffc20f",
                                            boxShadow: "0 4px 12px rgba(109, 22, 117, 0.3)",
                                        }}
                                    >
                                        {icon}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ 
                                                fontWeight: "bold", 
                                                color: "#6d1675",
                                                fontSize: "16px",
                                                mb: 1,
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ 
                                                color: "#666",
                                                lineHeight: 1.6,
                                                fontSize: "14px",
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </motion.div>
                        ))}
                    </Stack>
                </Grid>
            </Grid>

            {/* Keyframes animation */}
            <style>
                {`
          @keyframes shinyMove {
            0% {
              left: -75%;
            }
            100% {
              left: 125%;
            }
          }
        `}
            </style>
        </Box>
    );
};

export default Trataka;